import { clone, stringify } from '#library/serialize.library.js';
import { asType } from '#library/type.library.js';
import { isIntegerLike, isArrayLike, isDefined, isInteger, isIterable, isNullish, isString, isUndefined, isNumber, isNumeric, isError, isObject } from '#library/assertion.library.js';
import { trimAll } from '#library/string.library.js';
export function asArray(arr = [], fill) {
    const mapFn = (val) => (isUndefined(fill) || isDefined(val)) ? val : clone(fill);
    return (isArrayLike(arr) || (isIterable(arr) && !isString(arr)))
        ? Array.from(arr, mapFn)
        : [arr];
}
/**
 * Coerces a value to a string using serialization if it is not nullish.
 *
 * @param str - The value to coerce to a string
 * @returns The stringified value, or an empty string if nullish
 * @example
 * ```ts
 * const str = asString({ a: 1 }); // '{"a":1}'
 * ```
 */
export function asString(str) {
    return isNullish(str)
        ? ''
        : isInteger(str)
            ? str.toString() + 'n'
            : stringify(str);
}
export function asText(str, fallback, deepClean = false) {
    if (isString(str)) {
        const cleaned = deepClean ? trimAll(str) : str.trim();
        if (cleaned.length > 0)
            return cleaned;
    }
    return fallback;
}
export function when(value, guard, fallback) {
    return guard(value) ? value : fallback;
}
export function asNumber(val, fallback) {
    if (isNumber(val))
        return val;
    if (isInteger(val)) {
        const num = Number(val);
        if (isNumber(num))
            return num;
        return fallback;
    }
    if (isString(val)) {
        const trimmed = val.trim();
        if (trimmed.length > 0) {
            const parsed = Number(trimmed);
            if (isNumber(parsed))
                return parsed;
        }
    }
    return fallback;
}
const RE_INTEGER = /^[+-]?[0-9]+$/;
const RE_SIGN_START = /^[+-]/;
const MAX_SAFE_INTEGER_BIGINT = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE_INTEGER_BIGINT = BigInt(Number.MIN_SAFE_INTEGER);
/**
 * @param str - The value with optional plus|minus prefix
 * @returns The string value without the sign prefix
 */
function removeSign(str) {
    return String(str).trim().replace(RE_SIGN_START, '');
}
/**
 * Parses any `string | number | bigint` to a canonical signed `bigint`.
 * Handles optional sign prefix, trailing `n` suffix (BigInt literal), and float truncation.
 * Does **not** apply a safe-integer boundary check — that is `toBounded`'s responsibility.
 */
function parseBigInt(str) {
    if (isInteger(str))
        return str; // already a bigint
    if (isNumber(str))
        return BigInt(Math.trunc(str)); // truncate float, no string round-trip
    const raw = String(str).trim();
    const clean = removeSign(str);
    if (clean.length === 0)
        return BigInt(raw);
    const sign = raw.startsWith('-') ? -1n : 1n; // preserve sign before stripping
    const trim = isIntegerLike(clean) ? -1 : undefined; // strip trailing 'n' (undefined = keep full string)
    return sign * BigInt(clean.slice(0, trim));
}
/**
 * Applies the safe-integer boundary check to a `bigint`.
 * Returns a `number` when the value fits within `Number.MAX_SAFE_INTEGER` bounds,
 * otherwise preserves the `bigint` to avoid precision loss.
 */
function toBounded(big) {
    return (big > MAX_SAFE_INTEGER_BIGINT || big < MIN_SAFE_INTEGER_BIGINT)
        ? big
        : Number(big);
}
/**
 * Coerces a String or Number to a BigInt.
 *
 * @param str - The value to coerce
 * @returns The coerced BigInt
 * @example
 * ```ts
 * const big = asInteger(123.45); // 123n
 * ```
 */
export function asInteger(str) {
    const arg = asType(str);
    switch (arg.type) {
        case 'BigInt':
            return arg.value; // already a BigInt
        case 'Number':
            return BigInt(Math.trunc(arg.value)); // cast as BigInt
        case 'String':
            return parseBigInt(arg.value); // normalise sign + optional trailing 'n'
        default:
            return str;
    }
}
/**
 * Returns the value as a Number or BigInt if possible, otherwise returns the original string.
 *
 * @param str - The string, number, or bigint to process
 * @param stripZero - Whether to strip leading zeros when evaluating numeric strings
 * @returns The numeric coercion or the original string
 * @example
 * ```ts
 * const num = ifNumeric('123'); // 123
 * ```
 */
export const ifNumeric = (str, stripZero = false) => {
    if (isInteger(str) || isNumber(str)) // native BigInt or finite number → boundary-check
        return toBounded(parseBigInt(str));
    const value = removeSign(str); // only reached for string input
    if (isNumeric(str) && (!value.startsWith('0') || stripZero))
        return (isIntegerLike(value) || RE_INTEGER.test(value)) // BigInt literal or plain integer string
            ? toBounded(parseBigInt(str))
            : asNumber(str, 0); // floating-point string
    return str; // non-numeric or leading-zero string → as-is
};
/**
 * Returns the value if defined, otherwise 0.
 *
 * @param obj - The value to check
 * @returns The value or 0
 * @example
 * ```ts
 * const val = nullishToZero(null); // 0
 * ```
 */
export const nullishToZero = (obj) => obj ?? 0;
/**
 * Returns the value if defined, otherwise an empty string.
 *
 * @param obj - The value to check
 * @returns The value or an empty string
 * @example
 * ```ts
 * const val = nullishToEmpty(undefined); // ''
 * ```
 */
export const nullishToEmpty = (obj) => obj ?? '';
/**
 * Returns the value if defined, otherwise the fallback value.
 *
 * @param obj - The value to check
 * @param value - The fallback value
 * @returns The original value or the fallback value
 * @example
 * ```ts
 * const val = nullishToValue(null, 'default'); // 'default'
 * ```
 */
export const nullishToValue = (obj, value) => obj ?? value;
/**
 * Coerces an unknown value into a proper Error instance.
 * Preserves the name, message, stack, and code if available.
 *
 * @param err - The unknown error value
 * @returns An Error instance
 * @example
 * ```ts
 * const err = asError('Something went wrong');
 * ```
 */
export function asError(err) {
    if (isError(err))
        return err;
    const error = new Error(isObject(err) && isString(err.message) ? err.message : String(err));
    if (isObject(err)) {
        error.name = isString(err.name) ? err.name : 'Error';
        if ('code' in err && (isString(err.code) || isNumber(err.code)))
            error.code = err.code;
        if ('stack' in err && isString(err.stack))
            error.stack = err.stack;
    }
    return error;
}
